import { useQueries } from '@tanstack/react-query';
import { creatorbaseQueries } from '@round/api';
import { useInfiniteTeams, InfiniteTeamsConfig as TeamsConfig } from '@round/creatorbase-helpers/api-hooks';
import { useFeatureFlags } from 'utility/featureFlags/FeatureFlagsContext';

const campaignsOptions = creatorbaseQueries.campaigns.options.list;

export type CreditLimitData = {
    title: string;
    currencyId: number;
    creditUsed: number;
    maxCredit: number | null;
    teamId: number | null;
    unallocatedCampaignsCount: number | null;
    livePostsCreditUsed: number | null;
    livePostsCampaignCount: number | null;
    allApprovedCreditUsed: number | null;
    allApprovedCampaignCount: number | null;
};

type Params = {
    page_size: number;
    search: string;
    client_id: number;
};

type Config = TeamsConfig;

export default function useCreditLimitsData(params: Partial<Params>, config?: Config) {
    const featureFlags = useFeatureFlags();
    const isDetailedCreditLimitsEnabled = featureFlags.isEnabled('creatorbase_detailed_credit_limits');

    const {
        data,
        teams,
        isLoading,
        isFetching,
        isFetchingNextPage,
        isFetchingPreviousPage,
        errorMessage,
        hasNextPage,
        fetchNextPage,
        hasPreviousPage,
        fetchPreviousPage,
    } = useInfiniteTeams(
        { ...params, include_credit_breakdown: isDetailedCreditLimitsEnabled ? true : undefined },
        { refetchOnMount: 'always', ...config }
    );

    const teamIds = teams.map((t) => t.id);
    const campaigns = useQueries({
        queries: teamIds.map((id) => campaignsOptions({ team_id: id, page_size: 1 })),
        combine: (results) => {
            return results.map((res, index) => {
                const teamId = teamIds[index];
                return { teamId, unallocatedCampaignsCount: res.data?.count ?? null };
            });
        },
    });

    const creditLimitsData: CreditLimitData[] = teams.map((team) => ({
        title: team.name,
        currencyId: team.payment_credit_currency_id,
        creditUsed: team.current_payment_credit,
        maxCredit: team.max_payment_credit,
        teamId: team.id,
        unallocatedCampaignsCount: campaigns.find((c) => c.teamId === team.id)?.unallocatedCampaignsCount ?? null,
        livePostsCreditUsed: team.live_posts_credit_used,
        livePostsCampaignCount: team.live_posts_campaign_count,
        allApprovedCreditUsed: team.all_approved_credit_used,
        allApprovedCampaignCount: team.all_approved_campaign_count,
    }));

    return {
        data: creditLimitsData,
        count: data?.pages[0]?.count,
        pageParams: data?.pageParams,
        isLoading,
        isFetching,
        isFetchingNextPage,
        isFetchingPreviousPage,
        hasNextPage,
        fetchNextPage,
        hasPreviousPage,
        fetchPreviousPage,
        errorMessage,
    };
}
